import axios from 'axios';
import { apiBase } from 'services/apiBaseUrl';
import Http from 'services/Http';
import sanitizeAgenticBody from './sanitizeAgenticBody';
import { getActiveAuthHeaders } from 'utils/getActiveAuthHeaders';

const apiBaseURL = apiBase();

function authorizeRequest(config = {}) {
  const headers = { ...(config.headers || {}), ...getActiveAuthHeaders() };
  return { ...config, headers };
}

class AgentApi {
  list() {
    return Http.get('/v1/agents').then(r => r.data.agents);
  }

  get(id) {
    return Http.get(`/v1/agents/${id}`).then(r => r.data.agent);
  }

  create(body) {
    return Http.post('/v1/agents', sanitizeAgenticBody(body)).then(r => r.data.agent);
  }

  updateDraft(id, body) {
    return Http.put(`/v1/agents/${id}/draft`, sanitizeAgenticBody(body)).then(r => r.data.agent);
  }

  previewStart(id, { modality = 'text' } = {}) {
    return Http.post(`/v1/agents/${id}/preview/start`, { modality }).then(r => r.data);
  }

  previewMessage(id, conversationId, text, history = [], { modality = 'text' } = {}) {
    return Http.post(`/v1/agents/${id}/preview/${conversationId}/messages`, {
      text,
      history,
      modality,
    }).then(r => r.data);
  }

  voiceToken(id, appUserId) {
    return Http.post(`/v1/agents/${id}/voice/token`, { appUserId }).then(r => r?.data ?? r);
  }

  destroy(id, { deleteExclusive = false } = {}) {
    const config = deleteExclusive ? { params: { deleteExclusive: true } } : undefined;
    return Http.delete(`/v1/agents/${id}`, config);
  }

  deletePreview(id) {
    return Http.get(`/v1/agents/${id}/delete-preview`).then(r => r.data.preview);
  }

  listBuiltinTools() {
    return Http.get('/v1/agents/builtin-tools').then(r => r.data.tools);
  }

  exportGraph(id) {
    return Http.get(`/v1/agents/${id}/export/graph`).then(r => r.data);
  }

  async exportBundle(id, exportKeys) {
    const response = await axios.post(
      `${apiBaseURL}/v1/agents/${id}/export`,
      { exportKeys },
      {
        ...authorizeRequest(),
        responseType: 'blob',
      },
    );
    return response.data;
  }

  async importPreview(file, namePrefix = '') {
    const formData = new FormData();
    formData.append('bundle', file);
    if (namePrefix) formData.append('namePrefix', namePrefix);
    const response = await axios.post(`${apiBaseURL}/v1/agents/import/preview`, formData, {
      ...authorizeRequest({ headers: { 'Content-Type': 'multipart/form-data' } }),
    });
    return response.data?.data;
  }

  async importConfirm(file, { resolutions = {}, namePrefix = '', renameNames = {} } = {}) {
    const formData = new FormData();
    formData.append('bundle', file);
    formData.append('resolutions', JSON.stringify(resolutions));
    formData.append('renameNames', JSON.stringify(renameNames));
    if (namePrefix) formData.append('namePrefix', namePrefix);
    const response = await axios.post(`${apiBaseURL}/v1/agents/import`, formData, {
      ...authorizeRequest({ headers: { 'Content-Type': 'multipart/form-data' } }),
    });
    return response.data?.data;
  }
}

export default new AgentApi();
